import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Building2, Users, CreditCard, Wrench, Key, UserCheck } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Building2,
      title: "Property Management",
      description: "Manage multiple properties with ease"
    },
    {
      icon: Users,
      title: "Tenant Portal", 
      description: "Secure tenant access for payments and maintenance"
    },
    {
      icon: CreditCard,
      title: "Payment Processing",
      description: "Stripe and PayPal integration for rent collection"
    },
    {
      icon: Wrench,
      title: "Maintenance Workflow",
      description: "Streamlined work orders with contractor estimates"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Building2 className="h-8 w-8 text-blue-600" />
              <h1 className="text-2xl font-bold text-gray-900">Legacy Property Association</h1>
            </div>
            <div className="flex space-x-3">
              <Button variant="outline" onClick={() => navigate('/tenant')}>
                Tenant Portal
              </Button>
              <Button onClick={() => navigate('/owner')}>
                Owner Login
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Complete Property Management Solution
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Streamline your property management with our comprehensive platform designed for landlords and tenants.
          </p>
        </div>

        {/* Access Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {/* Owner/Investor Card */}
          <Card className="hover:shadow-lg transition-shadow duration-200 border-2 border-blue-200">
            <CardHeader className="text-center pb-4">
              <div className="mx-auto mb-4 p-3 bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center">
                <UserCheck className="h-8 w-8 text-blue-600" />
              </div>
              <CardTitle className="text-2xl text-blue-900">Owner/Investor Portal</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-600 text-center mb-6">
                Access your property management dashboard with comprehensive tools for landlords and property investors.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center text-sm">
                  <div className="h-2 w-2 bg-blue-600 rounded-full mr-3"></div>
                  Manage properties and tenants
                </li>
                <li className="flex items-center text-sm">
                  <div className="h-2 w-2 bg-blue-600 rounded-full mr-3"></div>
                  Process payments and collect rent
                </li>
                <li className="flex items-center text-sm">
                  <div className="h-2 w-2 bg-blue-600 rounded-full mr-3"></div>
                  Handle work orders
                </li>
                <li className="flex items-center text-sm">
                  <div className="h-2 w-2 bg-blue-600 rounded-full mr-3"></div>
                  Generate reports and analytics
                </li>
              </ul>
              <Button 
                className="w-full" 
                size="lg"
                onClick={() => navigate('/owner')}
              >
                Access Owner Portal
              </Button>
            </CardContent>
          </Card>

          {/* Tenant Card */}
          <Card className="hover:shadow-lg transition-shadow duration-200 border-2 border-green-200">
            <CardHeader className="text-center pb-4">
              <div className="mx-auto mb-4 p-3 bg-green-100 rounded-full w-16 h-16 flex items-center justify-center">
                <Key className="h-8 w-8 text-green-600" />
              </div>
              <CardTitle className="text-2xl text-green-900">Tenant Portal</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-600 text-center mb-6">
                Access your tenant dashboard using your Tenant ID and Access Code provided by your landlord.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center text-sm">
                  <div className="h-2 w-2 bg-green-600 rounded-full mr-3"></div>
                  Pay rent online securely
                </li>
                <li className="flex items-center text-sm">
                  <div className="h-2 w-2 bg-green-600 rounded-full mr-3"></div>
                  Submit work orders
                </li>
                <li className="flex items-center text-sm">
                  <div className="h-2 w-2 bg-green-600 rounded-full mr-3"></div>
                  View payment history
                </li>
                <li className="flex items-center text-sm">
                  <div className="h-2 w-2 bg-green-600 rounded-full mr-3"></div>
                  Update contact information
                </li>
              </ul>
              <Button 
                className="w-full bg-green-600 hover:bg-green-700" 
                size="lg"
                onClick={() => navigate('/tenant')}
              >
                Access Tenant Portal
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Features Section */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-center text-gray-900 mb-8">Platform Features</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="mx-auto mb-4 p-3 bg-white rounded-lg shadow-sm w-16 h-16 flex items-center justify-center">
                  <feature.icon className="h-8 w-8 text-blue-600" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">{feature.title}</h4>
                <p className="text-sm text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-gray-600">
            <p>&copy; 2024 Legacy Property Association. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;